/**
 * RoundedPolygon — core shape class for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.RoundedPolygon.kt
 *
 * A RoundedPolygon is defined by a list of Feature (Corner + Edge) objects.
 * Its geometry is stored as a flat list of Cubic Bézier curves that form
 * a closed, contiguous outline.
 */

import { type CornerRounding, UNROUNDED } from "../math/corner-rounding";
import { Cubic } from "../math/cubic";
import { type Point, point } from "../math/point";
import {
	convex,
	DISTANCE_EPSILON,
	distance,
	distanceSquared,
	FLOAT_PI,
	interpolate,
	type PointTransformer,
	radialToCartesian,
	square,
} from "../utils/utils";
import {
	cornerFeature,
	edgeFeature,
	type Feature,
	transformFeature,
} from "./features";

// ─── Internal: RoundedCorner ─────────────────────────────────────────────────

/**
 * Internal helper that computes the Bézier curves for a single rounded corner.
 * Ported from the private RoundedCorner class in RoundedPolygon.kt
 */
class RoundedCorner {
	private readonly d1: Point;
	private readonly d2: Point;
	private readonly cornerRadius: number;
	private readonly smoothing: number;
	private readonly sinAngle: number;
	readonly expectedRoundCut: number;
	private center: Point = point(0, 0);

	constructor(
		private readonly p0: Point,
		private readonly p1: Point,
		private readonly p2: Point,
		readonly rounding: CornerRounding = UNROUNDED,
	) {
		const v01x = p0.x - p1.x,
			v01y = p0.y - p1.y;
		const v21x = p2.x - p1.x,
			v21y = p2.y - p1.y;
		const d01 = distance(v01x, v01y);
		const d21 = distance(v21x, v21y);

		if (d01 > 0 && d21 > 0) {
			this.d1 = point(v01x / d01, v01y / d01);
			this.d2 = point(v21x / d21, v21y / d21);
			this.cornerRadius = rounding.radius;
			this.smoothing = rounding.smoothing;
			const cosA = this.d1.x * this.d2.x + this.d1.y * this.d2.y;
			this.sinAngle = Math.sqrt(Math.max(0, 1 - square(cosA)));
			this.expectedRoundCut =
				this.sinAngle > 1e-3
					? (this.cornerRadius * (cosA + 1)) / this.sinAngle
					: 0;
		} else {
			this.d1 = point(0, 0);
			this.d2 = point(0, 0);
			this.cornerRadius = 0;
			this.smoothing = 0;
			this.sinAngle = 0;
			this.expectedRoundCut = 0;
		}
	}

	get expectedCut(): number {
		return (1 + this.smoothing) * this.expectedRoundCut;
	}

	getCubics(allowedCut0: number, allowedCut1 = allowedCut0): Cubic[] {
		const allowedCut = Math.min(allowedCut0, allowedCut1);
		if (
			this.expectedRoundCut < DISTANCE_EPSILON ||
			allowedCut < DISTANCE_EPSILON ||
			this.cornerRadius < DISTANCE_EPSILON
		) {
			this.center = this.p1;
			return [Cubic.straightLine(this.p1.x, this.p1.y, this.p1.x, this.p1.y)];
		}

		const actualRoundCut = Math.min(allowedCut, this.expectedRoundCut);
		const actualSmoothing0 = this.calcSmoothingValue(allowedCut0);
		const actualSmoothing1 = this.calcSmoothingValue(allowedCut1);
		const actualR =
			(this.cornerRadius * actualRoundCut) / this.expectedRoundCut;
		const centerDist = Math.sqrt(square(actualR) + square(actualRoundCut));

		// Center of rounding arc
		const midDirX = (this.d1.x + this.d2.x) / 2;
		const midDirY = (this.d1.y + this.d2.y) / 2;
		const midDist = distance(midDirX, midDirY);
		this.center = point(
			this.p1.x + (midDirX / midDist) * centerDist,
			this.p1.y + (midDirY / midDist) * centerDist,
		);

		const ci0 = point(
			this.p1.x + this.d1.x * actualRoundCut,
			this.p1.y + this.d1.y * actualRoundCut,
		);
		const ci2 = point(
			this.p1.x + this.d2.x * actualRoundCut,
			this.p1.y + this.d2.y * actualRoundCut,
		);

		const flanking0 = this.computeFlankingCurve(
			actualRoundCut,
			actualSmoothing0,
			this.p1,
			this.p0,
			ci0,
			ci2,
			this.center,
			actualR,
		);
		const flanking2 = this.computeFlankingCurve(
			actualRoundCut,
			actualSmoothing1,
			this.p1,
			this.p2,
			ci2,
			ci0,
			this.center,
			actualR,
		).reverse();

		return [
			flanking0,
			Cubic.circularArc(
				this.center.x,
				this.center.y,
				flanking0.anchor1X,
				flanking0.anchor1Y,
				flanking2.anchor0X,
				flanking2.anchor0Y,
			),
			flanking2,
		];
	}

	private calcSmoothingValue(allowedCut: number): number {
		if (allowedCut > this.expectedCut) return this.smoothing;
		if (allowedCut > this.expectedRoundCut) {
			return (
				(this.smoothing * (allowedCut - this.expectedRoundCut)) /
				(this.expectedCut - this.expectedRoundCut)
			);
		}
		return 0;
	}

	private computeFlankingCurve(
		actualRoundCut: number,
		actualSmoothing: number,
		corner: Point,
		sideStart: Point,
		circleIntersect: Point,
		otherIntersect: Point,
		circleCenter: Point,
		actualR: number,
	): Cubic {
		const sdx = sideStart.x - corner.x,
			sdy = sideStart.y - corner.y;
		const sd = distance(sdx, sdy);
		const sideDir = point(sdx / sd, sdy / sd);
		const curveStart = point(
			corner.x + sideDir.x * actualRoundCut * (1 + actualSmoothing),
			corner.y + sideDir.y * actualRoundCut * (1 + actualSmoothing),
		);

		// Interpolate on the circle arc for the end point
		const px = interpolate(
			circleIntersect.x,
			(circleIntersect.x + otherIntersect.x) / 2,
			actualSmoothing,
		);
		const py = interpolate(
			circleIntersect.y,
			(circleIntersect.y + otherIntersect.y) / 2,
			actualSmoothing,
		);

		// Project onto circle
		const ptoCx = px - circleCenter.x,
			ptoCy = py - circleCenter.y;
		const ptoCd = distance(ptoCx, ptoCy);
		const curveEnd = point(
			circleCenter.x + (ptoCx / ptoCd) * actualR,
			circleCenter.y + (ptoCy / ptoCd) * actualR,
		);

		// Tangent at curveEnd
		const tangentX = -(curveEnd.y - circleCenter.y);
		const tangentY = curveEnd.x - circleCenter.x;

		// Line intersection to find anchor end
		const anchorEnd =
			lineIntersection(
				sideStart,
				sideDir,
				curveEnd,
				point(tangentX, tangentY),
			) ?? circleIntersect;
		const anchorStart = point(
			(curveStart.x + anchorEnd.x * 2) / 3,
			(curveStart.y + anchorEnd.y * 2) / 3,
		);

		return new Cubic([
			curveStart.x,
			curveStart.y,
			anchorStart.x,
			anchorStart.y,
			anchorEnd.x,
			anchorEnd.y,
			curveEnd.x,
			curveEnd.y,
		]);
	}
}

function lineIntersection(
	p0: Point,
	d0: Point,
	p1: Point,
	d1: Point,
): Point | null {
	// d1 rotated 90°
	const rd1x = -d1.y,
		rd1y = d1.x;
	const den = d0.x * rd1x + d0.y * rd1y;
	if (Math.abs(den) < DISTANCE_EPSILON) return null;
	const num = (p1.x - p0.x) * rd1x + (p1.y - p0.y) * rd1y;
	if (Math.abs(den) < DISTANCE_EPSILON * Math.abs(num)) return null;
	const k = num / den;
	return point(p0.x + d0.x * k, p0.y + d0.y * k);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function calculateCenter(vertices: number[]): Point {
	let cx = 0,
		cy = 0;
	for (let i = 0; i < vertices.length; i += 2) {
		cx += vertices[i];
		cy += vertices[i + 1];
	}
	const n = vertices.length / 2;
	return point(cx / n, cy / n);
}

function verticesFromNumVerts(
	numVertices: number,
	radius: number,
	cx: number,
	cy: number,
): number[] {
	const result: number[] = [];
	for (let i = 0; i < numVertices; i++) {
		const angle = (FLOAT_PI / numVertices) * 2 * i;
		const [rx, ry] = radialToCartesian(radius, angle);
		result.push(cx + rx, cy + ry);
	}
	return result;
}

function starVerticesFromNumVerts(
	numVerticesPerRadius: number,
	radius: number,
	innerRadius: number,
	cx: number,
	cy: number,
): number[] {
	const result: number[] = [];
	const total = numVerticesPerRadius * 2;
	for (let i = 0; i < total; i++) {
		const r = i % 2 === 0 ? radius : innerRadius;
		const angle = (FLOAT_PI / numVerticesPerRadius) * i;
		const [rx, ry] = radialToCartesian(r, angle);
		result.push(cx + rx, cy + ry);
	}
	return result;
}

// ─── RoundedPolygon ──────────────────────────────────────────────────────────

/**
 * The core shape class. Represents a closed polygon outline as a list of Cubic
 * Bézier curves, optionally rounded at the vertices.
 *
 * All shapes in MD3 Expressive are represented as RoundedPolygon instances.
 *
 * @example
 * // Create a circle (8-vertex polygon, fully rounded)
 * const circle = RoundedPolygon.circle();
 *
 * @example
 * // Create a rounded rectangle
 * const rect = RoundedPolygon.rectangle(2, 1, { radius: 0.25, smoothing: 0 });
 */
export class RoundedPolygon {
	/** Flat list of cubic Bézier curves forming the closed outline. */
	readonly cubics: Cubic[];
	/** Feature list (corners + edges). */
	readonly features: Feature[];
	/** Center point of the polygon. */
	readonly center: Point;

	/** @internal Use static factory methods instead */
	private constructor(features: Feature[], center: Point) {
		this.features = features;
		this.center = center;
		this.cubics = buildCubicsList(features);
		validateContiguity(this.cubics);
	}

	get centerX(): number {
		return this.center.x;
	}
	get centerY(): number {
		return this.center.y;
	}

	// ─── Static factories ────────────────────────────────────────────────

	/**
	 * Creates a polygon from a vertex count (regular polygon).
	 * @param numVertices - Number of vertices (≥ 3)
	 * @param radius - Circumradius (default: 1)
	 * @param centerX - Center X (default: 0)
	 * @param centerY - Center Y (default: 0)
	 * @param rounding - Corner rounding for all vertices
	 * @param perVertexRounding - Per-vertex rounding overrides
	 */
	static fromNumVertices(
		numVertices: number,
		radius = 1,
		centerX = 0,
		centerY = 0,
		rounding: CornerRounding = UNROUNDED,
		perVertexRounding?: CornerRounding[],
	): RoundedPolygon {
		if (numVertices < 3)
			throw new Error("Polygon must have at least 3 vertices");
		const vertices = verticesFromNumVerts(
			numVertices,
			radius,
			centerX,
			centerY,
		);
		return RoundedPolygon.fromVertices(
			vertices,
			rounding,
			perVertexRounding,
			centerX,
			centerY,
		);
	}

	/**
	 * Creates a polygon from a flat vertex array [x0, y0, x1, y1, ...].
	 * @param vertices - Flat array of x,y pairs (length must be even and ≥ 6)
	 * @param rounding - Corner rounding for all vertices
	 * @param perVertexRounding - Per-vertex rounding overrides
	 * @param centerX - Center X (auto-calculated if not provided)
	 * @param centerY - Center Y (auto-calculated if not provided)
	 */
	static fromVertices(
		vertices: number[],
		rounding: CornerRounding = UNROUNDED,
		perVertexRounding?: CornerRounding[],
		centerX?: number,
		centerY?: number,
	): RoundedPolygon {
		if (vertices.length < 6)
			throw new Error("Polygon must have at least 3 vertices");
		if (vertices.length % 2 !== 0)
			throw new Error("Vertices array must have even length");
		if (perVertexRounding && perVertexRounding.length * 2 !== vertices.length) {
			throw new Error("perVertexRounding must match vertex count");
		}

		const n = vertices.length / 2;

		// Build rounded corners
		const roundedCorners: RoundedCorner[] = [];
		for (let i = 0; i < n; i++) {
			const vtxRounding = perVertexRounding ? perVertexRounding[i] : rounding;
			const prevIdx = ((i + n - 1) % n) * 2;
			const nextIdx = ((i + 1) % n) * 2;
			roundedCorners.push(
				new RoundedCorner(
					point(vertices[prevIdx], vertices[prevIdx + 1]),
					point(vertices[i * 2], vertices[i * 2 + 1]),
					point(vertices[nextIdx], vertices[nextIdx + 1]),
					vtxRounding,
				),
			);
		}

		// Calculate how much each side can accommodate rounding cuts
		const cutAdjusts: [number, number][] = [];
		for (let ix = 0; ix < n; ix++) {
			const expectedRoundCut =
				roundedCorners[ix].expectedRoundCut +
				roundedCorners[(ix + 1) % n].expectedRoundCut;
			const expectedCut =
				roundedCorners[ix].expectedCut +
				roundedCorners[(ix + 1) % n].expectedCut;
			const vx = vertices[ix * 2],
				vy = vertices[ix * 2 + 1];
			const nx = vertices[((ix + 1) % n) * 2],
				ny = vertices[((ix + 1) % n) * 2 + 1];
			const sideSize = distance(vx - nx, vy - ny);

			if (expectedRoundCut > sideSize) {
				cutAdjusts.push([sideSize / expectedRoundCut, 0]);
			} else if (expectedCut > sideSize) {
				cutAdjusts.push([
					1,
					(sideSize - expectedRoundCut) / (expectedCut - expectedRoundCut),
				]);
			} else {
				cutAdjusts.push([1, 1]);
			}
		}

		// Generate cubics per corner
		const corners: Cubic[][] = [];
		for (let i = 0; i < n; i++) {
			const allowedCuts: number[] = [];
			for (const delta of [0, 1]) {
				const [roundCutRatio, cutRatio] = cutAdjusts[(i + n - 1 + delta) % n];
				allowedCuts.push(
					roundedCorners[i].expectedRoundCut * roundCutRatio +
						(roundedCorners[i].expectedCut -
							roundedCorners[i].expectedRoundCut) *
							cutRatio,
				);
			}
			corners.push(roundedCorners[i].getCubics(allowedCuts[0], allowedCuts[1]));
		}

		// Build feature list
		const tempFeatures: Feature[] = [];
		for (let i = 0; i < n; i++) {
			const prevIdx = (i + n - 1) % n;
			const nextIdx = (i + 1) % n;
			const currV = point(vertices[i * 2], vertices[i * 2 + 1]);
			const prevV = point(vertices[prevIdx * 2], vertices[prevIdx * 2 + 1]);
			const nextV = point(vertices[nextIdx * 2], vertices[nextIdx * 2 + 1]);
			const isConvex = convex(
				prevV.x,
				prevV.y,
				currV.x,
				currV.y,
				nextV.x,
				nextV.y,
			);
			tempFeatures.push(cornerFeature(corners[i], isConvex));
			const lastCornerA1 = corners[i][corners[i].length - 1];
			const nextCornerA0 = corners[(i + 1) % n][0];
			tempFeatures.push(
				edgeFeature([
					Cubic.straightLine(
						lastCornerA1.anchor1X,
						lastCornerA1.anchor1Y,
						nextCornerA0.anchor0X,
						nextCornerA0.anchor0Y,
					),
				]),
			);
		}

		const cx = centerX ?? calculateCenter(vertices).x;
		const cy = centerY ?? calculateCenter(vertices).y;
		return new RoundedPolygon(tempFeatures, point(cx, cy));
	}

	/**
	 * Creates a polygon from a pre-built Feature list.
	 * @param features - Feature list (≥ 2 features)
	 * @param centerX - Center X (auto-calculated if NaN)
	 * @param centerY - Center Y (auto-calculated if NaN)
	 */
	static fromFeatures(
		features: Feature[],
		centerX = Number.NaN,
		centerY = Number.NaN,
	): RoundedPolygon {
		if (features.length < 2)
			throw new Error("Polygon must have at least 2 features");
		const allVertices: number[] = [];
		for (const f of features) {
			for (const c of f.cubics) {
				allVertices.push(c.anchor0X, c.anchor0Y);
			}
		}
		const cx = Number.isNaN(centerX) ? calculateCenter(allVertices).x : centerX;
		const cy = Number.isNaN(centerY) ? calculateCenter(allVertices).y : centerY;
		return new RoundedPolygon(features, point(cx, cy));
	}

	// ─── Instance methods ────────────────────────────────────────────────

	/**
	 * Returns a new RoundedPolygon with all points transformed.
	 * @param fn - Transform function: (x, y) => [x', y']
	 */
	transformed(fn: PointTransformer): RoundedPolygon {
		const [cx, cy] = fn(this.center.x, this.center.y);
		return new RoundedPolygon(
			this.features.map((f) => transformFeature(f, fn)),
			point(cx, cy),
		);
	}

	/**
	 * Returns a normalized copy: scaled and centered to fit in [0,1] × [0,1].
	 * Maintains aspect ratio by using the larger dimension.
	 */
	normalized(): RoundedPolygon {
		const [minX, minY, maxX, maxY] = this.calculateBounds(false);
		const width = maxX - minX;
		const height = maxY - minY;
		const side = Math.max(width, height);
		const offsetX = (side - width) / 2 - minX;
		const offsetY = (side - height) / 2 - minY;
		return this.transformed((x, y) => [
			(x + offsetX) / side,
			(y + offsetY) / side,
		]);
	}

	/**
	 * Calculates the axis-aligned bounding box.
	 * @param approximate - If true, uses control point hull (faster). Default: true.
	 * @returns [minX, minY, maxX, maxY]
	 */
	calculateBounds(approximate = true): [number, number, number, number] {
		let minX = Number.MAX_VALUE,
			minY = Number.MAX_VALUE;
		let maxX = -Number.MAX_VALUE,
			maxY = -Number.MAX_VALUE;
		for (const c of this.cubics) {
			const [cx0, cy0, cx1, cy1] = c.calculateBounds(approximate);
			if (cx0 < minX) minX = cx0;
			if (cy0 < minY) minY = cy0;
			if (cx1 > maxX) maxX = cx1;
			if (cy1 > maxY) maxY = cy1;
		}
		return [minX, minY, maxX, maxY];
	}

	/**
	 * Calculates the maximum bounding square (useful for shapes that rotate).
	 * @returns [minX, minY, maxX, maxY] of the max-bounds square
	 */
	calculateMaxBounds(): [number, number, number, number] {
		let maxDistSq = 0;
		for (const c of this.cubics) {
			const ad = distanceSquared(
				c.anchor0X - this.centerX,
				c.anchor0Y - this.centerY,
			);
			const mid = c.pointOnCurve(0.5);
			const md = distanceSquared(mid.x - this.centerX, mid.y - this.centerY);
			const m = Math.max(ad, md);
			if (m > maxDistSq) maxDistSq = m;
		}
		const d = Math.sqrt(maxDistSq);
		return [
			this.centerX - d,
			this.centerY - d,
			this.centerX + d,
			this.centerY + d,
		];
	}
}

// ─── Internal: build cubics list from features ─────────────────────────────

function buildCubicsList(features: Feature[]): Cubic[] {
	const result: Cubic[] = [];
	let firstCubic: Cubic | null = null;
	let lastCubic: Cubic | null = null;
	let firstFeatureSplitStart: Cubic[] | null = null;
	let firstFeatureSplitEnd: Cubic[] | null = null;

	if (features.length > 0 && features[0].cubics.length === 3) {
		const center = features[0].cubics[1];
		const [start, end] = center.split(0.5);
		firstFeatureSplitStart = [features[0].cubics[0], start];
		firstFeatureSplitEnd = [end, features[0].cubics[2]];
	}

	for (let i = 0; i <= features.length; i++) {
		let featureCubics: Cubic[];
		if (i === 0 && firstFeatureSplitEnd) {
			featureCubics = firstFeatureSplitEnd;
		} else if (i === features.length) {
			if (firstFeatureSplitStart) featureCubics = firstFeatureSplitStart;
			else break;
		} else {
			featureCubics = features[i].cubics;
		}

		for (const cubic of featureCubics) {
			if (!cubic.zeroLength()) {
				if (lastCubic) result.push(lastCubic);
				lastCubic = cubic;
				if (!firstCubic) firstCubic = cubic;
			} else if (lastCubic) {
				// Update last anchor to maintain contiguity
				const p: number[] = lastCubic.points.slice();
				p[6] = cubic.anchor1X;
				p[7] = cubic.anchor1Y;
				lastCubic = new Cubic(p);
			}
		}
	}

	if (lastCubic && firstCubic) {
		result.push(
			new Cubic([
				lastCubic.anchor0X,
				lastCubic.anchor0Y,
				lastCubic.control0X,
				lastCubic.control0Y,
				lastCubic.control1X,
				lastCubic.control1Y,
				firstCubic.anchor0X,
				firstCubic.anchor0Y,
			]),
		);
	} else {
		result.push(Cubic.empty(0, 0));
	}

	return result;
}

function validateContiguity(cubics: Cubic[]): void {
	let prev = cubics[cubics.length - 1];
	for (const cubic of cubics) {
		if (
			Math.abs(cubic.anchor0X - prev.anchor1X) > DISTANCE_EPSILON ||
			Math.abs(cubic.anchor0Y - prev.anchor1Y) > DISTANCE_EPSILON
		) {
			throw new Error(
				`RoundedPolygon must be contiguous: gap between (${cubic.anchor0X},${cubic.anchor0Y}) and (${prev.anchor1X},${prev.anchor1Y})`,
			);
		}
		prev = cubic;
	}
}

// ─── Star vertex helper (used by Shapes.ts) ─────────────────────────────────

export { starVerticesFromNumVerts };
